import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';

import { iconUIComponent } from 'ctm-css-themes';
import { applyVerticalStyles, getClassName, shapedPropsWarning } from 'ctm-util-functions';

import { withStyleProxy } from '../../higher_order_components/with_style_proxy';
import styles from './icon.css';

/**
 * Icon React Component
 * @desc Icon component that allows to create Icons using <span> or <i> html elements.
 *       Allows to use variations based on the corresponding css modules variant.
 *       Render the component.
 *       When transferring props to a child component, you should ensure that you are not accidentally
 *       forwarding props that were intended to be interpreted by the parent component
 *       Only standard DOM attributes should be used when creating a native DOM node.
 *       Otherwise it would cause warnings like "Unknown props `visualProps` on <span> tag.
 *       More info here: https://facebook.github.io/react/warnings/unknown-prop.html
 * @param {Object} props React props
 * @returns {Object} - Returns the Icon React Component.
 */
const _Icon = (props) => {
    /**
     * Merging vertical specific CSS with the generic app css,
     * to avoid it being output in the app.css on every vertical.
     * The vertical specific CSS is exported from ctm-vertical-#/css and passed through the Route
     * to Page Wrapper.
     */
    // @todo remove once all shapedProps are gone
    shapedPropsWarning(props, 'Icon');

    const iconStyles = applyVerticalStyles(styles, 'icon');
    const { className, tagName, margin, UIComponent, display, variant, animation, onClick, color } = props;

    const faIconMap = {
        'variant_fa-chevron-right': faChevronRight
    };

    const marginStyle = margin ? `margin_${margin}` : null;
    const myUIComponent = UIComponent ? iconUIComponent[UIComponent] : null;

    const iconClassName = classNames(
        styles.root,
        className,
        myUIComponent,
        getClassName(iconStyles, `display_${display || 'inline_block'}`),
        getClassName(iconStyles, animation)
    );

    // Build shared event handlers
    const eventHandlers = {};
    if (typeof onClick === 'function') {
        eventHandlers.onClick = onClick;
    }

    // Check if this is a FontAwesome icon
    if (faIconMap[variant]) {
        const faProps = {
            ...eventHandlers,
            className: classNames(
                iconClassName,
                getClassName(iconStyles, margin ? `margin_${margin}_small` : null),
            )
        };

        if (color) {
            faProps.color = color;
        }

        return <FontAwesomeIcon size="xs" icon={faIconMap[variant]} className={iconClassName} {...faProps} />;
    }

    // Render standard CSS-based icon
    const type = tagName || 'span';
    const componentProps = {
        className: classNames(
            iconClassName,
            getClassName(iconStyles, marginStyle),
            getClassName(iconStyles, variant)
        ),
        ...eventHandlers
    };

    return React.createElement(type, componentProps);
};

_Icon.propTypes = {
    /**
     * Element properties
     */
    className: PropTypes.string,
    /**
     * Tag name of the element
     * 'span' and 'i' are specific for this component.
     */
    tagName: PropTypes.oneOf(['span', 'i']),
    display: PropTypes.string,
    UIComponent: PropTypes.string,
    variant: PropTypes.string,
    margin: PropTypes.oneOf(['left', 'right', 'left_right']),
    animation: PropTypes.string,
    onClick: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.object
    ])
};

export { _Icon };
export const Icon = withStyleProxy(_Icon);
